
const getStatusBadge = (status, serviceType) => {
    const isTransport = serviceType === "passenger" || serviceType === "transport";
    if (status === "delivered" || status === "completed") {
        return {
            label: isTransport ? "KOUS LA FINI" : "LIVREZON FÈT",
            cls: "bg-green-100 text-green-700 border-green-200"
        };
    }
    if (status === "on_the_way") {
        return {
            label: isTransport ? "AN WOUT AK PASAJE" : "AN WOUT",
            cls: "bg-orange-100 text-orange-600 border-orange-200"
        };
    }
    if (status === "arrived_pickup") {
        return {
            label: "RIVE KOTE PASAJE",
            cls: "bg-purple-100 text-purple-600 border-purple-200"
        };
    }
    if (status === "arrived_store") {
        return {
            label: "RIVE MAGAZEN",
            cls: "bg-purple-100 text-purple-600 border-purple-200"
        };
    }
    if (status === "picked_up") {
        return {
            label: isTransport ? "PASAJE A MONTE" : "PRAN KOLI",
            cls: "bg-amber-100 text-amber-600 border-amber-200"
        };
    }
    if (status === "accepted") {
        return {
            label: "ASEPTE",
            cls: "bg-blue-100 text-blue-600 border-blue-200"
        };
    }
    if (status === "pending_driver") {
        return {
            label: "SAN CHOFÈ",
            cls: "bg-red-100 text-red-600 border-red-200"
        };
    }
    if (status === "cancelled") {
        return {
            label: "ANILE",
            cls: "bg-gray-100 text-gray-500 border-gray-200"
        };
    }
    return {
        label: status || "ENKONI",
        cls: "bg-gray-100 text-gray-500 border-gray-200"
    };
};

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
    Truck, Package, User, Phone, MapPin, 
    Navigation, CheckCircle, 
    Clock, Search, ArrowRight, Loader2,
    Plus, Map, RefreshCw, CheckCheck, X, Trash2, Eye
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ===================== STATUS CONFIG =====================
const DELIVERY_STEPS = [
    { key: "accepted",       label: "Asepte Kous",              color: "text-blue-500",   bg: "bg-blue-50",    border: "border-blue-200" },
    { key: "arrived_store",  label: "Rive Magazen/Kote Depa",   color: "text-purple-500", bg: "bg-purple-50",  border: "border-purple-200" },
    { key: "picked_up",      label: "Pran Koli / Pasaje",       color: "text-amber-500",  bg: "bg-amber-50",   border: "border-amber-200" },
    { key: "on_the_way",     label: "An Wout",                  color: "text-orange-500", bg: "bg-orange-50",  border: "border-orange-200" },
    { key: "delivered",      label: "Kous la Fini",             color: "text-green-500",  bg: "bg-green-50",   border: "border-green-200" },
];

const TRANSPORT_STEPS = [
    { key: "accepted",       label: "Asepte Kous",              color: "text-blue-500",   bg: "bg-blue-50",    border: "border-blue-200" },
    { key: "arrived_pickup", label: "Rive Kote Pasaje a",       color: "text-purple-500", bg: "bg-purple-50",  border: "border-purple-200" },
    { key: "on_the_way",     label: "An Wout ak Pasaje",        color: "text-orange-500", bg: "bg-orange-50",  border: "border-orange-200" },
    { key: "delivered",      label: "Kous la Fini",             color: "text-green-500",  bg: "bg-green-50",   border: "border-green-200" },
];

const STATUS_BADGE = {
    pending_driver: { label: "San Chofe",        cls: "bg-red-100 text-red-600 border-red-200" },
    accepted:       { label: "Asepte",           cls: "bg-blue-100 text-blue-600 border-blue-200" },
    arrived_store:  { label: "Rive Magazen",     cls: "bg-purple-100 text-purple-600 border-purple-200" },
    arrived_pickup: { label: "Rive Kote Pasaje", cls: "bg-purple-100 text-purple-600 border-purple-200" },
    picked_up:      { label: "Pran Koli",        cls: "bg-amber-100 text-amber-600 border-amber-200" },
    on_the_way:     { label: "An Wout",          cls: "bg-orange-100 text-orange-600 border-orange-200" },
    delivered:      { label: "Livrezon Fet",     cls: "bg-green-100 text-green-600 border-green-200" },
    completed:      { label: "Konplete",         cls: "bg-green-100 text-green-600 border-green-200" },
    cancelled:      { label: "Anile",            cls: "bg-gray-100 text-gray-500 border-gray-200" },
};

const fmtTime = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleTimeString("fr-HT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return null; }
};

// ===================== TRACKING PANEL =====================
const TrackingPanel = ({ order, onClose }) => {
    const [track, setTrack] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchTrack = useCallback(async () => {
        if (!order?.id) return;
        try {
            const res = await axios.get(`${API}/drivers/dispatch/track/${order.id}`);
            setTrack(res.data);
        } catch (e) { console.error("Track fetch error:", e); }
        finally { setLoading(false); }
    }, [order?.id]);

    useEffect(() => {
        fetchTrack();
        const interval = setInterval(() => {
            setTrack(prev => {
                const st = prev?.status;
                if (!st || ["delivered","completed","cancelled"].includes(st)) return prev;
                fetchTrack();
                return prev;
            });
        }, 5000);
        return () => clearInterval(interval);
    }, [fetchTrack]);

    const steps = (track?.service_type === "passenger" || track?.service_type === "transport")
        ? TRANSPORT_STEPS : DELIVERY_STEPS;

    const activeIdx = (() => {
        if (!track) return -1;
        const st = track.status;
        if (["delivered","completed"].includes(st)) return steps.length - 1;
        const idx = steps.findIndex(s => s.key === st);
        return idx >= 0 ? idx : (track?.timeline?.accepted ? 0 : -1);
    })();

    const tlKey = (k, tl) => tl?.[k] || tl?.[`${k}_at`] || null;
    const hasGps = track?.driver_gps?.lat && track?.driver_gps?.lng;
    const mapsUrl = hasGps ? `https://www.google.com/maps?q=${track.driver_gps.lat},${track.driver_gps.lng}` : null;
    const badge = getStatusBadge(track?.status, track?.service_type);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-[#232F3E] px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Suivi Traje Chofe</p>
                        <h3 className="text-white font-black text-sm italic uppercase truncate">
                            {track?.product_title || order?.product_title || `Kous ${order?.id?.slice(0,8)}`}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={fetchTrack} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all" title="Rafrayi">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#FF9900] animate-spin" /></div>
                    ) : (<>
                        <div className="flex items-center justify-between">
                            <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${badge.cls}`}>{badge.label}</span>
                            {hasGps && (
                                <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9900] hover:bg-[#e68a00] text-black rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-md">
                                    <Map className="w-3 h-3" /> We sou Kat
                                </a>
                            )}
                        </div>
                        {track?.driver_info?.fullname && (
                            <div className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-[#232F3E] flex items-center justify-center text-white font-black text-base shrink-0 overflow-hidden">
                                    {track.driver_info.profile_photo
                                        ? <img src={track.driver_info.profile_photo} alt="" className="w-full h-full object-cover" />
                                        : track.driver_info.fullname[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-black text-gray-900 italic uppercase truncate">{track.driver_info.fullname}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <span className="text-[9px] font-black bg-[#FF9900]/10 text-[#FF9900] px-2 py-0.5 rounded-full uppercase tracking-widest">{track.driver_info.vehicle_type}</span>
                                        <span className="text-[10px] font-bold text-gray-400">{track.driver_info.phone}</span>
                                    </div>
                                </div>
                                {hasGps && (
                                    <div className="flex flex-col items-end">
                                        <div className="flex items-center gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                            <span className="text-[9px] font-black text-green-500 uppercase">GPS Aktif</span>
                                        </div>
                                        {track.driver_gps.last_seen && <span className="text-[8px] text-gray-400 mt-0.5">{fmtTime(track.driver_gps.last_seen)}</span>}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="space-y-2">
                            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-3">
                                <div className="w-6 h-6 rounded-lg bg-blue-500 flex items-center justify-center shrink-0 mt-0.5"><MapPin className="w-3 h-3 text-white" /></div>
                                <div>
                                    <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">{(track?.service_type === "passenger" || track?.service_type === "transport") ? "Kote Pasaje a ye (Depa)" : "Kote Depa / Magazen"}</p>
                                    <p className="text-xs font-bold text-gray-800">{track?.pickup_address || "—"}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 bg-green-50 rounded-xl p-3">
                                <div className="w-6 h-6 rounded-lg bg-green-500 flex items-center justify-center shrink-0 mt-0.5"><CheckCheck className="w-3 h-3 text-white" /></div>
                                <div>
                                    <p className="text-[8px] font-black text-green-400 uppercase tracking-widest">{(track?.service_type === "passenger" || track?.service_type === "transport") ? "Destinasyon Pasaje a" : "Destinasyon Koli a"}</p>
                                    <p className="text-xs font-bold text-gray-800">{track?.dropoff_address || "—"}</p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">Pwogre Kous La</p>
                            <div className="space-y-2">
                                {steps.map((step, idx) => {
                                    const done = idx <= activeIdx;
                                    const active = idx === activeIdx;
                                    const ts = fmtTime(tlKey(step.key, track?.timeline));
                                    return (
                                        <div key={step.key} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? `${step.bg} ${step.border} shadow-sm` : done ? "bg-gray-50 border-gray-100" : "bg-white border-gray-50 opacity-40"}`}>
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${done ? step.bg : "bg-gray-100"}`}>
                                                {done ? <CheckCircle className={`w-4 h-4 ${step.color}`} /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-[11px] font-black uppercase tracking-wide ${active ? step.color : done ? "text-gray-700" : "text-gray-300"}`}>{step.label}</p>
                                                {ts && <p className="text-[9px] text-gray-400 font-bold">{ts}</p>}
                                            </div>
                                            {active && <div className="w-2 h-2 rounded-full bg-[#FF9900] animate-pulse shrink-0" />}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {(track?.client_name || track?.client_phone) && (
                            <div className="bg-gray-50 rounded-2xl p-4">
                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">Enfòmasyon Kliyan</p>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs font-bold text-gray-700">{track.client_name}</span>
                                    {track.client_phone && <><Phone className="w-4 h-4 text-gray-400" /><span className="text-xs font-bold text-gray-700">{track.client_phone}</span></>}
                                </div>
                            </div>
                        )}

                        {/* ====== FOTO PREV ====== */}
                        {(track?.pickup_photo || track?.delivery_photo || track?.house_photo) && (
                            <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3">📸 Foto Prèv Chofe a</p>
                                <div className="grid grid-cols-3 gap-2">
                                    {track.pickup_photo && (
                                        <a href={track.pickup_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.pickup_photo} alt="Pickup" className="w-full h-24 object-cover rounded-xl border-2 border-amber-200 group-hover:border-amber-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase">📦 Magazen</span>
                                            </div>
                                        </a>
                                    )}
                                    {track.delivery_photo && (
                                        <a href={track.delivery_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.delivery_photo} alt="Livrezon" className="w-full h-24 object-cover rounded-xl border-2 border-green-200 group-hover:border-green-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-green-600 text-white px-1.5 py-0.5 rounded-full uppercase">✅ Koli</span>
                                            </div>
                                        </a>
                                    )}
                                    {track.house_photo && (
                                        <a href={track.house_photo} target="_blank" rel="noopener noreferrer" className="relative group block">
                                            <img src={track.house_photo} alt="Kay" className="w-full h-24 object-cover rounded-xl border-2 border-blue-200 group-hover:border-blue-400 transition-all shadow-sm" />
                                            <div className="absolute bottom-1 left-1 right-1 text-center">
                                                <span className="text-[7px] font-black bg-blue-600 text-white px-1.5 py-0.5 rounded-full uppercase">🏠 Kay</span>
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </>)}
                </div>
            </div>
        </div>
    );
};

// ===================== MAIN COMPONENT =====================
export const AdminDispatcher = () => {
    const [orders, setOrders] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showManualRide, setShowManualRide] = useState(false);
    const [manualRide, setManualRide] = useState({ customer_name: "", phone: "", address: "", destination: "", price_htg: "" });
    const [trackingOrder, setTrackingOrder] = useState(null);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const [ordersRes, driversRes] = await Promise.all([
                axios.get(`${API}/admin/marketplace/orders`),
                axios.get(`${API}/drivers/list-active`)
            ]);
            setOrders(ordersRes.data);
            setDrivers(driversRes.data.drivers || []);
        } catch (error) { console.error("Dispatcher fetch error:", error); }
        finally { setLoading(false); }
    };

    const handleAssign = async (orderId, driverId) => {
        setAssigning(driverId);
        try {
            const res = await axios.post(`${API}/drivers/assign-order/${orderId}`, { driver_id: driverId });
            if (res.data.success) { toast.success("Komand asiyen avek sikse!"); setSelectedOrder(null); fetchData(); }
        } catch (error) { toast.error(error.response?.data?.detail || "Echkek nan asiyasyon an."); }
        finally { setAssigning(null); }
    };

    const handleUnassign = async (orderId) => {
        if (!window.confirm("Ou asire ou vle retire komand sa nan men chofe a?")) return;
        try {
            const res = await axios.post(`${API}/drivers/unassign-order/${orderId}`);
            if (res.data.success) { toast.success("Chofe retire avek sikse!"); setSelectedOrder(null); fetchData(); }
        } catch (error) { toast.error(error.response?.data?.detail || "Echkek."); }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm("Sa ap efase komand sa net nan sistem nan. Ou asire?")) return;
        try {
            const res = await axios.delete(`${API}/admin/marketplace/orders/${orderId}`);
            if (res.data.success) { toast.success("Komand efase!"); setSelectedOrder(null); fetchData(); }
        } catch (error) { toast.error(error.response?.data?.detail || "Echkek pou efase komand."); }
    };

    const handleCreateManualRide = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/admin/dispatch/manual-ride`, {
                customer_name: manualRide.customer_name, phone: manualRide.phone,
                address: manualRide.address, destination: manualRide.destination,
                price_htg: parseFloat(manualRide.price_htg) || 0
            });
            toast.success("Kous kreye avek sikse!");
            setManualRide({ customer_name: "", phone: "", address: "", destination: "", price_htg: "" });
            setShowManualRide(false); fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || "Echkek pou kreye kous."); }
    };

    const activeOrders = orders.filter(o => {
        const s = o.delivery_info?.status || o.status;
        return o.delivery_info?.driver_id && !["delivered","completed","cancelled"].includes(s);
    });
    const pendingOrders = orders.filter(o => !o.delivery_info?.driver_id && !["cancelled","completed"].includes(o.status));
    const filteredDrivers = drivers.filter(d =>
        !searchQuery || d.fullname?.toLowerCase().includes(searchQuery.toLowerCase()) || d.phone?.includes(searchQuery)
    );
    const getBadge = (order) => {
        const s = order.delivery_info?.status || order.status || "pending_driver";
        return STATUS_BADGE[s] || { label: s, cls: "bg-gray-100 text-gray-500 border-gray-200" };
    };

    if (loading) return (
        <AdminLayout>
            <div className="flex items-center justify-center h-96"><Loader2 className="w-10 h-10 text-[#FF9900] animate-spin" /></div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            {trackingOrder && <TrackingPanel order={trackingOrder} onClose={() => setTrackingOrder(null)} />}
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-xl font-black text-[#232F3E] italic uppercase tracking-tight">Dispatch Hub</h1>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Sistèm Distribisyon Vizyèl an tan Reyèl</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-2.5">
                            <Clock className="w-4 h-4 text-amber-500" />
                            <div><p className="text-[8px] font-black text-amber-400 uppercase tracking-widest">Komand San Asiyasyon</p><p className="text-lg font-black text-amber-600 leading-none">{pendingOrders.length}</p></div>
                        </div>
                        <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-2xl px-4 py-2.5">
                            <Truck className="w-4 h-4 text-green-500" />
                            <div><p className="text-[8px] font-black text-green-400 uppercase tracking-widest">Chofe Disponib</p><p className="text-lg font-black text-green-600 leading-none">{drivers.length}</p></div>
                        </div>
                        {activeOrders.length > 0 && (
                            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-2.5">
                                <Navigation className="w-4 h-4 text-blue-500 animate-pulse" />
                                <div><p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Kous Aktif</p><p className="text-lg font-black text-blue-600 leading-none">{activeOrders.length}</p></div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* ====== KOMAND FINI AK FOTO ====== */}
                    <div className="lg:col-span-12 bg-white rounded-[30px] border border-gray-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-black italic uppercase text-gray-800 flex items-center gap-2">
                                <div className="w-1 h-5 bg-green-500 rounded-full" />
                                Komand Fini ({orders.filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status)).length})
                            </h2>
                        </div>
                        {orders.filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status)).length === 0 ? (
                            <div className="py-10 text-center">
                                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Pa gen komand fini kounye a.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto custom-scrollbar pr-1">
                                {orders
                                    .filter(o => ['delivered','completed'].includes(o.delivery_info?.status || o.status))
                                    .slice(0, 30)
                                    .map(order => {
                                        const del = order.delivery_info || {};
                                        const badge = getBadge(order);
                                        const pickupPhoto = del.pickup_photo || null;
                                        const deliveryPhoto = del.delivery_photo || del.package_photo || null;
                                        const housePhoto = del.house_photo || null;
                                        const hasPhotos = pickupPhoto || deliveryPhoto || housePhoto;
                                        const deliveredAt = del.delivered_at || del.completed_at || order.created_at;
                                        return (
                                            <div key={order.id || order.order_id} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all">
                                                <div className="p-4 flex items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${badge.cls}`}>{badge.label}</span>
                                                        </div>
                                                        <h4 className="text-sm font-black text-gray-900 italic uppercase truncate">{order.product_title || 'Komand'}</h4>
                                                        <p className="text-[9px] text-gray-400 font-bold mt-0.5">
                                                            {del.driver_name || ''} {del.driver_phone ? ('· ' + del.driver_phone) : ''}
                                                        </p>
                                                        {deliveredAt && (
                                                            <p className="text-[8px] text-gray-300 font-bold">
                                                                {new Date(deliveredAt).toLocaleString('fr-HT', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                                        <p className="text-sm font-black text-gray-800 italic">{(order.price_htg || del.fee || 0).toLocaleString()} HTG</p>
                                                        <div className="flex items-center gap-1.5">
                                                            <button
                                                                onClick={() => setTrackingOrder(order)}
                                                                className="flex items-center gap-1 px-2.5 py-1 bg-[#232F3E] hover:bg-black text-white rounded-lg text-[8px] font-black uppercase tracking-wider transition-all"
                                                            >
                                                                <Eye className="w-3 h-3" /> Foto
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteOrder(order.id || order.order_id)}
                                                                className="flex items-center gap-1 px-2.5 py-1 bg-red-50 hover:bg-red-500 text-red-500 hover:text-white rounded-lg text-[8px] font-black uppercase tracking-wider transition-all border border-red-100"
                                                                title="Efase Komand"
                                                            >
                                                                <Trash2 className="w-3 h-3" /> Fase
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {hasPhotos && (
                                                    <div className="px-4 pb-4">
                                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-2">Foto Prev</p>
                                                        <div className="flex gap-2">
                                                            {pickupPhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={pickupPhoto} alt="Pickup" className="w-16 h-16 object-cover rounded-xl border-2 border-amber-200 group-hover:border-amber-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-amber-500 text-white px-1 py-0.5 rounded-full uppercase">Magazen</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {deliveryPhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={deliveryPhoto} alt="Koli" className="w-16 h-16 object-cover rounded-xl border-2 border-green-200 group-hover:border-green-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-green-600 text-white px-1 py-0.5 rounded-full uppercase">Koli</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {housePhoto && (
                                                                <button onClick={() => setTrackingOrder(order)} className="relative group">
                                                                    <img src={housePhoto} alt="Kay" className="w-16 h-16 object-cover rounded-xl border-2 border-blue-200 group-hover:border-blue-400 transition-all" />
                                                                    <div className="absolute bottom-0.5 left-0.5 right-0.5 text-center">
                                                                        <span className="text-[6px] font-black bg-blue-600 text-white px-1 py-0.5 rounded-full uppercase">Kay</span>
                                                                    </div>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        )}
                    </div>

                    {/* LEFT PANEL */}
                    <div className="lg:col-span-4 space-y-4">
                        {activeOrders.length > 0 && (
                            <div>
                                <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse inline-block" />
                                    Kous Aktif — An Suivi ({activeOrders.length})
                                </p>
                                <div className="space-y-2 custom-scrollbar max-h-72 overflow-y-auto pr-1">
                                    {activeOrders.map(order => {
                                        const badge = getStatusBadge(order.delivery_info?.status || order.status, order.delivery_info?.service_type || order.service_type);
                                        const driverName = drivers.find(d => d.id === order.delivery_info?.driver_id)?.fullname || order.delivery_info?.driver_name || "—";
                                        return (
                                            <div key={order.id} onClick={() => setTrackingOrder(order)}
                                                className="bg-white rounded-2xl border border-blue-100 p-4 cursor-pointer hover:shadow-md transition-all group">
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <p className="text-[11px] font-black text-gray-800 italic truncate">{order.product_title}</p>
                                                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[8px] font-black uppercase border ${badge.cls}`}>{badge.label}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-5 h-5 rounded-lg bg-[#232F3E] flex items-center justify-center text-white text-[8px] font-black shrink-0">{driverName[0]}</div>
                                                        <span className="text-[10px] font-bold text-gray-600 truncate">{driverName}</span>
                                                    </div>
                                                    <span className="text-[9px] font-black text-blue-500 flex items-center gap-1"><Map className="w-3 h-3" /> Suiv</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                            Komand ki Bezwen Chofe ({pendingOrders.length})
                        </p>
                        <div className="space-y-2 custom-scrollbar max-h-[50vh] overflow-y-auto pr-1">
                            {pendingOrders.length === 0 ? (
                                <div className="py-12 text-center bg-white rounded-[30px] shadow border border-gray-50">
                                    <Package className="w-10 h-10 text-gray-100 mx-auto mb-3" />
                                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest italic">Pa gen komand ki ap tann</p>
                                </div>
                            ) : pendingOrders.map(order => (
                                <div key={order.id} onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                                    className={`bg-white rounded-2xl p-4 cursor-pointer border transition-all shadow-sm hover:shadow-md ${selectedOrder?.id === order.id ? "border-[#FF9900] ring-1 ring-[#FF9900]/30 shadow-md" : "border-gray-100"}`}>
                                    <div className="flex items-start justify-between gap-2 mb-1.5">
                                        <p className="text-[11px] font-black text-gray-800 italic truncate flex-1">{order.product_title}</p>
                                        <span className="shrink-0 text-[8px] font-black text-[#FF9900] bg-[#FF9900]/10 px-2 py-0.5 rounded-full">{order.price_htg ? `${order.price_htg} HTG` : ""}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                        <p className="text-[10px] font-bold text-gray-500 truncate">{order.delivery_info?.address || order.delivery_info?.pickup_address || "—"}</p>
                                    </div>
                                    {selectedOrder?.id === order.id && (
                                        <div className="flex gap-2 mt-3 pt-2 border-t border-gray-50">
                                            {order.delivery_info?.driver_id && (
                                                <Button variant="outline" size="sm" className="flex-1 text-[9px] uppercase font-black text-amber-600 border-amber-200 hover:bg-amber-50 h-8"
                                                    onClick={(e) => { e.stopPropagation(); handleUnassign(order.id); }}>Kansle Chofe</Button>
                                            )}
                                            <Button variant="outline" size="sm" className="flex-1 text-[9px] uppercase font-black text-red-600 border-red-200 hover:bg-red-50 h-8"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id); }}>Efase</Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT PANEL */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="flex justify-end">
                            <Button onClick={() => setShowManualRide(!showManualRide)}
                                className="bg-[#232F3E] hover:bg-black text-white font-black text-[10px] tracking-widest rounded-xl hover:scale-105 transition-all shadow-xl">
                                <Plus className="w-4 h-4 mr-2" />
                                {showManualRide ? "Femen Fom Nan" : "Kreye Kous Manyel (An Liy)"}
                            </Button>
                        </div>
                        {showManualRide && (
                            <form onSubmit={handleCreateManualRide} className="bg-white p-6 rounded-[30px] shadow-2xl border-2 border-[#FF9900]/20 space-y-4">
                                <h3 className="font-black text-sm uppercase text-[#232F3E] italic tracking-widest">Kreye yon Kous Manyel</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <input type="text" placeholder="Non Kliyan an" required value={manualRide.customer_name} onChange={(e) => setManualRide({...manualRide, customer_name: e.target.value})} className="h-10 px-4 rounded-xl border border-gray-200 text-xs font-black" />
                                    <input type="tel" placeholder="Telefon Kliyan an" required value={manualRide.phone} onChange={(e) => setManualRide({...manualRide, phone: e.target.value})} className="h-10 px-4 rounded-xl border border-gray-200 text-xs font-black" />
                                    <input type="text" placeholder="Adres Kliyan (Kote lap pran machin nan)" required value={manualRide.address} onChange={(e) => setManualRide({...manualRide, address: e.target.value})} className="h-10 px-4 rounded-xl border border-gray-200 text-xs font-black col-span-2" />
                                    <input type="text" placeholder="Destinasyon (Kote lale)" value={manualRide.destination} onChange={(e) => setManualRide({...manualRide, destination: e.target.value})} className="h-10 px-4 rounded-xl border border-gray-200 text-xs font-black col-span-2" />
                                    <input type="number" placeholder="Pri Kous la (HTG)" required value={manualRide.price_htg} onChange={(e) => setManualRide({...manualRide, price_htg: e.target.value})} className="h-10 px-4 rounded-xl border border-gray-200 text-xs font-black col-span-2" />
                                </div>
                                <Button type="submit" className="w-full h-12 bg-[#FF9900] text-black hover:bg-[#e68a00] font-black uppercase text-[10px] tracking-widest rounded-xl">
                                    Voye Kous la Bay Chofe Yo
                                </Button>
                            </form>
                        )}
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-2">
                            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400 italic">
                                {selectedOrder ? `Asiyen yon chofe pou [${selectedOrder.id.slice(0,8)}]` : "Chofe ki An Liy Kounye a"}
                            </h2>
                            <div className="relative w-full md:w-72">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type="text" placeholder="Chache chofe (Non, Telefon)..." value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full h-10 pl-11 pr-4 bg-white border border-gray-100 rounded-full text-xs font-bold focus:ring-2 focus:ring-[#FF9900] outline-none transition-all shadow-sm" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredDrivers.length === 0 ? (
                                <div className="col-span-full py-32 text-center bg-white rounded-[40px] shadow-xl border border-gray-50 flex flex-col items-center">
                                    <Truck className="w-16 h-16 text-gray-100 mb-6 italic" />
                                    <p className="text-[11px] font-black text-gray-300 uppercase tracking-[0.3em] italic">Pa gen chofe ki disponib kounye a</p>
                                </div>
                            ) : filteredDrivers.map(driver => {
                                const driverActive = activeOrders.find(o => o.delivery_info?.driver_id === driver.id);
                                return (
                                    <Card key={driver.id} className={`bg-white rounded-[35px] border-0 shadow-lg hover:shadow-2xl transition-all overflow-hidden ${!selectedOrder ? "opacity-90 hover:opacity-100" : ""} ${driverActive ? "ring-2 ring-blue-200" : ""}`}>
                                        <div className="p-6">
                                            <div className="flex items-center gap-5 mb-6">
                                                <div className="relative">
                                                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 border border-gray-50">
                                                        {driver.profile_photo
                                                            ? <img src={driver.profile_photo} alt={driver.fullname} className="w-full h-full object-cover" />
                                                            : <div className="w-full h-full flex items-center justify-center bg-[#232F3E] text-white font-black text-xl italic">{driver.fullname[0]}</div>}
                                                    </div>
                                                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center ${driverActive ? "bg-blue-500" : "bg-green-500"}`}>
                                                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-base font-black text-gray-900 italic truncate uppercase">{driver.fullname}</h3>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[9px] font-black bg-[#FF9900]/10 text-[#FF9900] px-2 py-0.5 rounded-full uppercase tracking-widest">{driver.vehicle_type}</span>
                                                        <span className="text-[10px] font-bold text-gray-400">{driver.phone}</span>
                                                    </div>
                                                    {driverActive && <span className="text-[8px] font-black text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full mt-1 inline-block uppercase tracking-widest">An Kous</span>}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-blue-500 shadow-sm"><Navigation className="w-4 h-4" /></div>
                                                    <div className="min-w-0">
                                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Estati</p>
                                                        <p className="text-[10px] font-black text-gray-900 italic truncate">{driverActive ? "An Kous" : "Disponib"}</p>
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-amber-500 shadow-sm"><Truck className="w-4 h-4" /></div>
                                                    <div className="min-w-0">
                                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Plate No</p>
                                                        <p className="text-[10px] font-black text-gray-900 italic truncate uppercase">{driver.vehicle_details?.slice(0,8) || "-"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            {driverActive && (
                                                <Button onClick={() => setTrackingOrder(driverActive)}
                                                    className="w-full h-10 bg-blue-500 hover:bg-blue-600 text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow transition-all flex items-center justify-center gap-2 mb-2">
                                                    <Map className="w-4 h-4" /> Suiv Traje Chofe a
                                                </Button>
                                            )}
                                            {selectedOrder && !selectedOrder.delivery_info?.driver_id && (
                                                <Button onClick={() => handleAssign(selectedOrder.id, driver.id)} disabled={assigning === driver.id}
                                                    className="w-full h-12 bg-[#232F3E] hover:bg-black text-white font-black rounded-2xl uppercase text-[10px] tracking-widest shadow-xl transition-all flex items-center justify-center gap-2">
                                                    {assigning === driver.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Asiyen Chofe <ArrowRight className="w-4 h-4" /></>}
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                    </div>
                </div>
            </div>
            <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar{width:4px}.custom-scrollbar::-webkit-scrollbar-track{background:transparent}.custom-scrollbar::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:10px}.custom-scrollbar::-webkit-scrollbar-thumb:hover{background:#d1d5db}` }} />
        </AdminLayout>
    );
};
